import { ModalForm, ProFormText, ProFormTextArea, ProFormSelect } from '@ant-design/pro-components';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, DatePicker, Divider, Form, Input, InputNumber, Modal, notification, Select, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import axiosApiInstance from '../../../api/axiosClient';
import { useAtomValue } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import type { Brand, Campaign, CampaignDeliverable, DeliverablePlatform, DeliverablePostType } from '../Campaigns/types';

const PLATFORM_OPTIONS = [
    { label: 'YouTube', value: 'yt' },
    { label: 'Instagram', value: 'ig' },
    { label: 'TikTok', value: 'tt' },
];
const POST_TYPE_OPTIONS = [
    { label: 'Photo', value: 'image' },
    { label: 'Video', value: 'video' },
    { label: 'Text', value: 'text' },
    { label: 'Story', value: 'story' },
    { label: 'Stream', value: 'stream' },
];

const DeliverableTemplateSection: React.FC<{
    value: CampaignDeliverable[];
    onChange: (v: CampaignDeliverable[]) => void;
}> = ({ value, onChange }) => {
    const update = (i: number, patch: Partial<CampaignDeliverable>) =>
        onChange(value.map((d, j) => (j === i ? { ...d, ...patch } : d)));
    const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
    const add = () => onChange([...value, {
        platform: 'yt' as DeliverablePlatform,
        post_type: 'video' as DeliverablePostType,
        quantity: 1,
    }]);

    const topGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 100px 36px',
        gap: '6px 10px',
        alignItems: 'center',
    };

    return (
        <div>
            {value.length > 0 && (
                <div style={{ ...topGridStyle, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>Platform</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>Post Type</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>How Many?</span>
                    <span />
                </div>
            )}
            {value.map((d, i) => (
                <div key={i} style={{ marginBottom: 12, borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>
                    <div style={topGridStyle}>
                        <Select
                            value={d.platform}
                            options={PLATFORM_OPTIONS}
                            onChange={(v) => update(i, { platform: v as DeliverablePlatform })}
                            style={{ width: '100%' }}
                        />
                        <Select
                            value={d.post_type}
                            options={POST_TYPE_OPTIONS}
                            onChange={(v) => update(i, { post_type: v as DeliverablePostType })}
                            style={{ width: '100%' }}
                        />
                        <InputNumber
                            min={1}
                            max={99}
                            value={d.quantity}
                            onChange={(v) => update(i, { quantity: v ?? 1 })}
                            style={{ width: '100%' }}
                        />
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(i)} />
                    </div>
                </div>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={add} style={{ marginTop: 4 }}>
                Add New
            </Button>
        </div>
    );
};

type Props = {
    creatorIds: number[];
    onClearSelection?: () => void;
};

const AddCreatorsToCampaign = (props: Props) => {
    const queryClient = useAtomValue(queryClientAtom);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [search, setSearch] = useState('');
    const [selectedCampaignId, setSelectedCampaignId] = useState<number | undefined>(undefined);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // New campaign inline creation state
    const [newCampaignModalOpen, setNewCampaignModalOpen] = useState(false);
    const [newCampaignName, setNewCampaignName] = useState('');
    const [newCampaignBrandId, setNewCampaignBrandId] = useState<number | undefined>(undefined);
    const [newCampaignStatus, setNewCampaignStatus] = useState('spec');
    const [newCampaignBudget, setNewCampaignBudget] = useState<number | null>(null);
    const [newCampaignBrief, setNewCampaignBrief] = useState('');
    const [newCampaignDeliverableTemplates, setNewCampaignDeliverableTemplates] = useState<CampaignDeliverable[]>([]);
    const [newCampaignStartDate, setNewCampaignStartDate] = useState<Dayjs | null>(null);
    const [newCampaignEndDate, setNewCampaignEndDate] = useState<Dayjs | null>(null);
    const [creatingCampaign, setCreatingCampaign] = useState(false);

    // Brands state (for the new-campaign form)
    const [brands, setBrands] = useState<Brand[]>([]);
    const [brandSearch, setBrandSearch] = useState('');
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [newBrandModalOpen, setNewBrandModalOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState('');
    const [creatingBrand, setCreatingBrand] = useState(false);

    // Fetch brands (debounced)
    useEffect(() => {
        const handle = setTimeout(() => {
            setLoadingBrands(true);
            axiosApiInstance
                .get<Brand[]>('/api/campaigns/brands', {
                    params: { search: brandSearch || undefined }
                })
                .then(({ data }) => setBrands(data ?? []))
                .catch((err) => console.error('brand search failed', err))
                .finally(() => setLoadingBrands(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [brandSearch]);

    const handleCreateBrand = async () => {
        if (!newBrandName.trim()) return;
        setCreatingBrand(true);
        try {
            const { data: created } = await axiosApiInstance.post<Brand>(
                '/api/campaigns/brands',
                { name: newBrandName.trim() }
            );
            notification.success({ message: `Brand "${created.name}" created` });
            setBrands((prev) => [created, ...prev]);
            setNewCampaignBrandId(created.id);
            setNewBrandName('');
            setNewBrandModalOpen(false);
        } catch (err: any) {
            notification.error({
                message: 'Failed to create brand',
                description: err?.response?.data?.error ?? err?.message,
            });
        } finally {
            setCreatingBrand(false);
        }
    };

    const brandOptions = brands.map((b) => ({ label: b.name, value: b.id }));

    // Fetch campaigns with optional search, debounced
    useEffect(() => {
        const handle = setTimeout(() => {
            setLoadingCampaigns(true);
            axiosApiInstance
                .get<Campaign[]>('/api/campaigns/items', {
                    params: { search: search || undefined }
                })
                .then(({ data }) => setCampaigns(data ?? []))
                .catch((err) => console.error('campaign search failed', err))
                .finally(() => setLoadingCampaigns(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [search]);

    const refreshCampaigns = () => {
        setLoadingCampaigns(true);
        axiosApiInstance
            .get<Campaign[]>('/api/campaigns/items')
            .then(({ data }) => setCampaigns(data ?? []))
            .catch((err) => console.error('campaign refresh failed', err))
            .finally(() => setLoadingCampaigns(false));
    };

    const handleCreateNewCampaign = async () => {
        if (!newCampaignName.trim() || newCampaignBrandId === undefined) return;
        setCreatingCampaign(true);
        try {
            const { data: created } = await axiosApiInstance.post<Campaign>(
                '/api/campaigns/items',
                {
                    name: newCampaignName.trim(),
                    status: newCampaignStatus,
                    brand_id: newCampaignBrandId,
                    budget: newCampaignBudget,
                    brief: newCampaignBrief,
                    deliverables2: newCampaignDeliverableTemplates,
                    start_date: newCampaignStartDate ? newCampaignStartDate.toISOString() : null,
                    end_date: newCampaignEndDate ? newCampaignEndDate.toISOString() : null,
                }
            );
            notification.success({ message: `Campaign "${created.name}" created` });
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
            // Add the newly created campaign to the local list and auto-select it
            setCampaigns((prev) => [created, ...prev]);
            setSelectedCampaignId(created.id);
            setNewCampaignName('');
            setNewCampaignBrandId(undefined);
            setNewCampaignStatus('spec');
            setNewCampaignBudget(null);
            setNewCampaignBrief('');
            setNewCampaignDeliverableTemplates([]);
            setNewCampaignStartDate(null);
            setNewCampaignEndDate(null);
            setNewCampaignModalOpen(false);
        } catch (err: any) {
            notification.error({
                message: 'Failed to create campaign',
                description: err?.response?.data?.error ?? err?.message,
            });
        } finally {
            setCreatingCampaign(false);
        }
    };

    const campaignOptions = campaigns.map((c) => ({
        label: c.name + (c.project ? ` — ${c.project}` : ''),
        value: c.id!,
    }));

    return (
        <>
        <ModalForm
            width={520}
            title={`Add ${props.creatorIds.length} Creator${props.creatorIds.length > 1 ? 's' : ''} to Campaign`}
            trigger={<Typography.Link>Add to Campaign</Typography.Link>}
            autoFocusFirstInput
            modalProps={{
                destroyOnClose: true,
                okText: 'Add to Campaign',
                cancelText: 'Cancel',
            }}
            submitter={{
                submitButtonProps: { disabled: selectedCampaignId === undefined, loading: submitting },
            }}
            onOpenChange={(open) => {
                if (open) refreshCampaigns();
                else {
                    setSelectedCampaignId(undefined);
                    setSearch('');
                    setNewCampaignName('');
                    setNewCampaignBrandId(undefined);
                    setNewCampaignStatus('spec');
                    setNewCampaignBudget(null);
                    setNewCampaignBrief('');
                    setNewCampaignDeliverableTemplates([]);
                    setNewCampaignStartDate(null);
                    setNewCampaignEndDate(null);
                }
            }}
            onFinish={async () => {
                if (selectedCampaignId === undefined) return false;
                setSubmitting(true);
                try {
                    await axiosApiInstance.post(
                        `/api/campaigns/items/${selectedCampaignId}/creators`,
                        { creator_ids: props.creatorIds }
                    );
                    notification.success({
                        message: 'Success',
                        description: `${props.creatorIds.length} creator${props.creatorIds.length > 1 ? 's' : ''} added to campaign.`,
                    });
                    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
                    if (props.onClearSelection) props.onClearSelection();
                    setSelectedCampaignId(undefined);
                    return true;
                } catch (error: any) {
                    notification.error({
                        message: 'Error',
                        description:
                            error?.response?.data?.error ?? 'Failed to add creators to campaign.',
                    });
                    return false;
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            <Select
                showSearch
                allowClear
                placeholder="Select a campaign or Create new…"
                filterOption={false}
                loading={loadingCampaigns}
                onSearch={setSearch}
                options={campaignOptions}
                value={selectedCampaignId}
                onChange={(val) => setSelectedCampaignId(val)}
                notFoundContent={loadingCampaigns ? 'Searching…' : 'No campaigns found'}
                style={{ width: '100%' }}
                dropdownRender={(menu) => (
                    <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <div style={{ padding: '0 8px 4px' }}>
                            <Button
                                type="link"
                                icon={<PlusOutlined />}
                                style={{ paddingLeft: 0 }}
                                onClick={() => setNewCampaignModalOpen(true)}
                            >
                                Create new campaign
                            </Button>
                        </div>
                    </>
                )}
            />
        </ModalForm>

        {/* Nested modal for quick campaign creation */}
        <Modal
            title="New Campaign"
            open={newCampaignModalOpen}
            width={600}
            okText="Create"
            cancelText="Cancel"
            okButtonProps={{ disabled: !newCampaignName.trim() || newCampaignBrandId === undefined, loading: creatingCampaign }}
            onOk={handleCreateNewCampaign}
            onCancel={() => {
                setNewCampaignModalOpen(false);
                setNewCampaignName('');
                setNewCampaignBrandId(undefined);
                setNewCampaignStatus('spec');
                setNewCampaignBudget(null);
                setNewCampaignBrief('');
                setNewCampaignDeliverableTemplates([]);
                setNewCampaignStartDate(null);
                setNewCampaignEndDate(null);
            }}
            destroyOnClose
        >
            <Form layout="vertical" style={{ marginTop: 8 }}>
                <Form.Item label="Campaign Name" required>
                    <Input
                        autoFocus
                        placeholder="e.g. Lidl Easter"
                        value={newCampaignName}
                        onChange={(e) => setNewCampaignName(e.target.value)}
                    />
                </Form.Item>
                <Form.Item label="Brand" required>
                    <Select
                        showSearch
                        allowClear
                        placeholder="Select a brand…"
                        filterOption={false}
                        loading={loadingBrands}
                        onSearch={setBrandSearch}
                        options={brandOptions}
                        value={newCampaignBrandId}
                        onChange={(val) => setNewCampaignBrandId(val)}
                        notFoundContent={loadingBrands ? 'Searching…' : 'No brands found'}
                        style={{ width: '100%' }}
                        dropdownRender={(menu) => (
                            <>
                                {menu}
                                <Divider style={{ margin: '8px 0' }} />
                                <div style={{ padding: '0 8px 4px' }}>
                                    <Button
                                        type="link"
                                        icon={<PlusOutlined />}
                                        style={{ paddingLeft: 0 }}
                                        onClick={() => setNewBrandModalOpen(true)}
                                    >
                                        Create new brand
                                    </Button>
                                </div>
                            </>
                        )}
                    />
                </Form.Item>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                    <Form.Item label="Status">
                        <Select
                            value={newCampaignStatus}
                            onChange={(v) => setNewCampaignStatus(v)}
                            options={[
                                { label: 'Spec', value: 'spec' },
                                { label: 'Active', value: 'active' },
                                { label: 'Completed', value: 'completed' },
                                { label: 'Canceled', value: 'canceled' },
                            ]}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item label="Budget (€)">
                        <InputNumber
                            min={0}
                            precision={2}
                            placeholder="0.00"
                            value={newCampaignBudget}
                            onChange={(v) => setNewCampaignBudget(v)}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                    <Form.Item label="Start Date">
                        <DatePicker
                            value={newCampaignStartDate}
                            onChange={(d) => setNewCampaignStartDate(d)}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item label="End Date">
                        <DatePicker
                            value={newCampaignEndDate}
                            onChange={(d) => setNewCampaignEndDate(d)}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </div>
                <Form.Item label="Brief">
                    <Input.TextArea
                        rows={3}
                        value={newCampaignBrief}
                        onChange={(e) => setNewCampaignBrief(e.target.value)}
                    />
                </Form.Item>
                <Form.Item label="Default Deliverables" extra="Assigned to every creator added to this campaign.">
                    <DeliverableTemplateSection
                        value={newCampaignDeliverableTemplates}
                        onChange={setNewCampaignDeliverableTemplates}
                    />
                </Form.Item>
            </Form>
        </Modal>

        {/* Create brand modal (nested inside create campaign modal) */}
        <Modal
            title="New Brand"
            open={newBrandModalOpen}
            okText="Create"
            cancelText="Cancel"
            okButtonProps={{ disabled: !newBrandName.trim(), loading: creatingBrand }}
            onOk={handleCreateBrand}
            onCancel={() => { setNewBrandModalOpen(false); setNewBrandName(''); }}
            destroyOnClose
        >
            <Form layout="vertical" style={{ marginTop: 8 }}>
                <Form.Item label="Brand Name" required>
                    <Input
                        autoFocus
                        placeholder="e.g. Lidl"
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                        onPressEnter={handleCreateBrand}
                    />
                </Form.Item>
            </Form>
        </Modal>
        </>
    );
};

export default AddCreatorsToCampaign;
